import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Gauge, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const Pillar = ({ icon: Icon, title, desc, accent }) => (
  <div className="group relative p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70 backdrop-blur transition-all hover:border-[#6B8DB3]/60 hover:bg-[#1C1F26]">
    <div
      className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
      style={{ background: accent }}
    >
      <Icon size={22} className="text-white" />
    </div>
    <div className="text-white font-semibold text-lg mb-1.5">{title}</div>
    <div className="text-sm text-[#9aa3ad] leading-relaxed">{desc}</div>
  </div>
);

const Welcome = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050608] text-white">
      {/* Header */}
      <header className="border-b border-[#1C1F26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Button
            data-testid="header-admin-btn"
            variant="ghost"
            className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26]"
            onClick={() => navigate("/admin/login")}
          >
            Acesso ADM
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-grid-faint opacity-60"
          aria-hidden="true"
        />
        <div
          className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #B9262B 0%, transparent 60%)" }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #013A63 0%, transparent 60%)" }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-slide">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1C1F26] border border-[#3A3F47] text-xs uppercase tracking-[0.28em] text-[#6B8DB3] mb-6">
                <Sparkles size={14} className="text-[#B9262B]" />
                Conhecimento que transforma
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                Diagnóstico de{" "}
                <span className="text-[#6B8DB3]">Maturidade</span> em{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Gestão de Frota</span>
                  <span
                    className="absolute left-0 right-0 bottom-1 h-3 bg-[#B9262B]/30 -z-0"
                    aria-hidden="true"
                  />
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#9aa3ad] max-w-2xl leading-relaxed">
                Avalie de forma rápida e estruturada a maturidade da gestão de
                frota da sua operação. Receba um <strong className="text-white">score</strong>, um{" "}
                <strong className="text-white">diagnóstico</strong> e um{" "}
                <strong className="text-white">plano de ação</strong> baseados em práticas
                de mercado.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="start-assessment-btn"
                  onClick={() => navigate("/diagnostico")}
                  className="bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold px-7 py-6 text-base rounded-lg glow-accent group"
                >
                  Iniciar Diagnóstico
                  <ArrowRight
                    size={18}
                    className="ml-2 transition-transform group-hover:translate-x-1"
                  />
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    document
                      .getElementById("como-funciona")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="border-[#3A3F47] text-white hover:bg-[#1C1F26] px-7 py-6 text-base rounded-lg"
                >
                  Como funciona
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-[#6B8DB3]">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22B673]" />
                  9 sessões avaliativas
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6B8DB3]" />
                  Escala 0 a 4 (Inexistente → Avançado)
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B9262B]" />
                  Score automático + plano de ação
                </div>
              </div>
            </div>

            {/* Right visual card */}
            <div className="lg:col-span-5 fade-slide" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-2xl border border-[#3A3F47] bg-[#0e1015] p-7 overflow-hidden">
                <div
                  className="absolute inset-0 opacity-30 bg-grid-faint"
                  aria-hidden="true"
                />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B8DB3] mb-2">
                    Score de Maturidade
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="text-7xl font-bold text-white leading-none">
                      82
                    </div>
                    <div className="pb-2 text-[#22B673] text-sm font-semibold">
                      Avançado
                    </div>
                  </div>
                  <div className="mt-6 space-y-3">
                    {[
                      { label: "Governança", v: 85, color: "#013A63" },
                      { label: "Manutenção", v: 78, color: "#B9262B" },
                      { label: "Combustível", v: 72, color: "#6B8DB3" },
                      { label: "Tecnologia", v: 88, color: "#22B673" },
                    ].map((row) => (
                      <div key={row.label}>
                        <div className="flex justify-between text-xs text-[#9aa3ad] mb-1">
                          <span>{row.label}</span>
                          <span className="font-mono text-white">{row.v}%</span>
                        </div>
                        <div className="h-1.5 bg-[#1C1F26] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${row.v}%`,
                              background: row.color,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="como-funciona" className="border-t border-[#1C1F26] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-3">
              Como funciona
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
              Um caminho rápido,{" "}
              <span className="text-[#B9262B]">claro</span> e{" "}
              <span className="text-[#6B8DB3]">recompensador</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <Pillar
              icon={ShieldCheck}
              title="1. Dados cadastrais"
              desc="Comece informando os dados da operação e do responsável pela frota."
              accent="#013A63"
            />
            <Pillar
              icon={Gauge}
              title="2. Sessões guiadas"
              desc="Responda perguntas por sessão com escala 0 a 4 e acompanhe seu progresso em tempo real."
              accent="#B9262B"
            />
            <Pillar
              icon={BarChart3}
              title="3. Score & plano"
              desc="Receba um diagnóstico estruturado, score por área e plano de ação consolidado."
              accent="#22B673"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1C1F26] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-[#6B8DB3]">
          <div>© {new Date().getFullYear()} UniGR · Conhecimento que transforma</div>
          <div>Transporte · Logística · Segurança · Inteligência de Dados</div>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
